import { useRef, useCallback } from 'react';
import type { CommentPinData } from '@corner/shared';

interface CommentPinsProps {
  pins: CommentPinData[];
  activePinId?: string | null;
  onPinClick?: (pinId: string) => void;
  onPinMove?: (pinId: string, xPercent: number, yPercent: number) => void;
  currentPage?: number;
}

const DRAG_THRESHOLD = 4; // px — movement below this is considered a click

/**
 * Renders comment pin markers on the document canvas.
 * Each pin is a small circle positioned at xPercent/yPercent.
 * Supports drag-to-reposition via pointer events.
 */
export default function CommentPins({ pins, activePinId, onPinClick, onPinMove, currentPage = 1 }: CommentPinsProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const dragState = useRef<{
    pinId: string;
    startX: number;
    startY: number;
    isDragging: boolean;
    el: HTMLElement;
  } | null>(null);

  const handlePointerDown = useCallback((e: React.PointerEvent, pinId: string) => {
    e.preventDefault();
    e.stopPropagation();
    const el = e.currentTarget as HTMLElement;
    el.setPointerCapture(e.pointerId);
    dragState.current = {
      pinId,
      startX: e.clientX,
      startY: e.clientY,
      isDragging: false,
      el,
    };
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    const state = dragState.current;
    if (!state) return;

    const dx = e.clientX - state.startX;
    const dy = e.clientY - state.startY;

    if (!state.isDragging) {
      if (Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD) return;
      state.isDragging = true;
      state.el.style.cursor = 'grabbing';
    }

    // Move the pin element directly for smooth dragging
    const overlay = overlayRef.current;
    if (!overlay) return;
    const rect = overlay.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    state.el.style.left = `${x * 100}%`;
    state.el.style.top = `${y * 100}%`;
  }, []);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    const state = dragState.current;
    if (!state) return;

    state.el.releasePointerCapture(e.pointerId);
    state.el.style.cursor = 'pointer';

    if (state.isDragging) {
      // Commit the new position
      const overlay = overlayRef.current;
      if (overlay && onPinMove) {
        const rect = overlay.getBoundingClientRect();
        const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
        onPinMove(state.pinId, x, y);
      }
    } else {
      // No drag — treat as click
      onPinClick?.(state.pinId);
    }

    dragState.current = null;
  }, [onPinClick, onPinMove]);

  if (pins.length === 0) return null;

  return (
    <div ref={overlayRef} style={OVERLAY}>
      {pins
        .filter((p) => p.pageNumber === currentPage)
        .map((pin) => {
          const isResolved = pin.status === 'resolved';
          const isActive = pin.pinId === activePinId;
          const replyCount = pin.comments.length - 1;
          const size = isResolved ? 24 : 28;

          return (
            <div
              key={pin.pinId}
              data-comment-interactive="true"
              onPointerDown={(e) => handlePointerDown(e, pin.pinId)}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              style={{
                position: 'absolute',
                left: `${pin.xPercent * 100}%`,
                top: `${pin.yPercent * 100}%`,
                transform: 'translate(-50%, -50%)',
                zIndex: isActive ? 52 : 51,
                cursor: 'pointer',
                pointerEvents: 'auto',
                touchAction: 'none',
              }}
            >
              <div
                style={{
                  width: size,
                  height: size,
                  borderRadius: '50%',
                  backgroundColor: isResolved ? '#9A8E82' : replyCount > 0 ? '#E05A33' : '#D97645',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: isResolved
                    ? '0 2px 6px rgba(0,0,0,0.15)'
                    : `0 2px 8px ${replyCount > 0 ? 'rgba(224,90,51,0.35)' : 'rgba(217,118,69,0.35)'}`,
                  transition: 'transform 0.15s ease',
                  ...(isActive ? { transform: 'scale(1.1)' } : {}),
                }}
              >
                {isResolved ? (
                  <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                    <path d="M3 5.5l2 2 3.5-4" stroke="#FFFFFF" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : replyCount > 0 ? (
                  <span style={{ fontFamily: '"Geist", system-ui, sans-serif', fontSize: 11, fontWeight: 700, color: '#FFFFFF' }}>
                    {pin.comments.length}
                  </span>
                ) : (
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M1.5 2h9A1 1 0 0111.5 3v5a1 1 0 01-1 1H4.5L1.5 11V3A1 1 0 012.5 2H1.5Z" fill="#FFFFFF" />
                  </svg>
                )}
              </div>
            </div>
          );
        })}
    </div>
  );
}

const OVERLAY: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
  overflow: 'visible',
  zIndex: 45,
};
